import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { BrandProvider } from "@/contexts/BrandContext";
import { LabelProvider } from "@/contexts/LabelContext";
import { Toaster } from "@/components/ui/sonner";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import { AlertTriangle } from "lucide-react";
import { hasCapability } from "@/services/api";
import ConciergeWidget from "@/components/ConciergeWidget";
// Public pages
import LandingPage from "@/pages/LandingPage";
import Login from "@/pages/Login";
import UATRolePicker from "@/pages/UATRolePicker";
import Verify2FA from "@/pages/Verify2FA";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import ActivateAccount from "@/pages/ActivateAccount";
import GoogleAuthCallback from "@/pages/GoogleAuthCallback";
import PublicInquiry from "@/pages/PublicInquiry";
// Prashasta Event Journey — Public Experience
import EventJourney from "@/pages/EventJourney";
import BuildMyEvent from "@/pages/BuildMyEvent";
// Prashasta Smart Event Studio — Adaptive Configurator (UX-01, feature-flagged)
import SmartStudio from "@/pages/SmartStudio";
import DemoOneEngage from "@/pages/DemoOneEngage";
import ExplorePage from "@/pages/ExplorePage";
import EventStoryPage from "@/pages/EventStoryPage";
import FindEventsPage from "@/pages/FindEventsPage";
// UX-HOME-01: Homepage Administration
import HomepageAdmin from "@/pages/HomepageAdmin";
// Core
import Dashboard from "@/pages/Dashboard";
import BusinessDashboard from "@/pages/BusinessDashboard";
import JourneyView from "@/pages/JourneyView";
import SetupStudio from "@/pages/SetupStudio";
import EventOpsMDMSetup from "@/pages/EventOpsMDMSetup";
import SetupDomainDetail from "@/pages/SetupDomainDetail";
import OrganizationSetup from "@/pages/OrganizationSetup";
import FinanceSetup from "@/pages/FinanceSetup";
import EventModelSetup from "@/pages/EventModelSetup";
import AgentGovernanceSetup from "@/pages/AgentGovernanceSetup";
// PRASHASTA-SETUP-02 — Gate 3: Business Setup hub
import BusinessSetup from "@/pages/BusinessSetup";
// CRM-MDM-01: CRM Configuration
import CRMConfiguration from "@/pages/CRMConfiguration";
import Events from "@/pages/Events";
import Customers from "@/pages/Customers";
import Administration from "@/pages/Administration";
import IntegrationControlCenter from "@/pages/IntegrationControlCenter";
import ImportManager from "@/pages/ImportManager";
import EnterpriseAdmin from "@/pages/EnterpriseAdmin";
// Stage 01 — ONE Engage
import EngageDashboard from "@/pages/EngageDashboard";
import Leads from "@/pages/Leads";
import LeadDetail from "@/pages/LeadDetail";
import Inbox from "@/pages/Inbox";
import FollowUps from "@/pages/FollowUps";
// Stage 01.1 — Email Channel
import EmailChannelSetup from "@/pages/EmailChannelSetup";
import CommunicationsAdmin from "@/pages/CommunicationsAdmin";
// ONE Engage CRM
import CRMAccounts from "@/pages/CRMAccounts";
import CRMAccountDetail from "@/pages/CRMAccountDetail";
import CRMContacts from "@/pages/CRMContacts";
import CRMContactDetail from "@/pages/CRMContactDetail";
import CRMOpportunities from "@/pages/CRMOpportunities";
import OpportunityDetail from "@/pages/OpportunityDetail";
import CRMActivities from "@/pages/CRMActivities";
import CommunicationInbox from "@/pages/CommunicationInbox";
// Wave A Sprint 2: A08 Bookings, A06 Service Catalog
import BookingDetail from "@/pages/BookingDetail";
import BookingsList from "@/pages/BookingsList";
import ServiceCatalog from "@/pages/ServiceCatalog";
// UEIM-02 — Structured Sourcing
import SupplierRegistry from "@/pages/SupplierRegistry";
import PartnerDirectory from "@/components/PartnerDirectory";
import SourcingPackageDetail from "@/pages/SourcingPackageDetail";
import SupplierQuoteForm from "@/pages/SupplierQuoteForm";
// Stage 02 — ONE Imagine
import ImagineDashboard from "@/pages/ImagineDashboard";
import EventImagine from "@/pages/EventImagine";
// Stage 03 — ONE Threads
import ThreadsView from "@/pages/ThreadsView";
import Event360 from "@/pages/Event360";
// EO-01 — Event Change Engine
import EventChangeDetail from "@/pages/EventChangeDetail";
// UAT Insight Agent
import UATControlCenter from "@/pages/UATControlCenter"; // preserved behind /uat route
// Event Planner / Designer
import PlannerCases from "@/pages/PlannerCases";
// UEIM-04: Event Core Workspace
import EventMasterWorkspace from "@/pages/EventMasterWorkspace";
import EventOperationsWorkspace from "@/pages/EventOperationsWorkspace";
import EventCloseoutWorkspace from "@/pages/EventCloseoutWorkspace";
import EventDocumentWorkspace from "@/pages/EventDocumentWorkspace";
import EventFinancialsWorkspace from "@/pages/EventFinancialsWorkspace";
import ApprovalPolicyAdminPage from "@/pages/ApprovalPolicyAdminPage";
// WVB-04: Financial Dashboard
import FinancialDashboard from "@/pages/FinancialDashboard";
import PurchaseOrderWorkspace from "@/pages/PurchaseOrderWorkspace";
import PurchaseOrderDetail from "@/pages/PurchaseOrderDetail";
// ── AP-PORTAL-01: Supplier Portal ─────────────────────────────────────────────
import SupplierLogin from "@/pages/SupplierLogin";
import SupplierPortalActivate from "@/pages/SupplierPortalActivate";
import SupplierPortalDashboard from "@/pages/SupplierPortalDashboard";
import SupplierPODetail from "@/pages/SupplierPODetail";
import PortalProtectedRoute from "@/components/PortalProtectedRoute";
// ── AP-ACCEPT-01: Supplier Delivery + Internal Acceptance ─────────────────────
import SupplierDeliveries from "@/pages/SupplierDeliveries";
import SupplierSubmitDelivery from "@/pages/SupplierSubmitDelivery";
import SupplierDeliveryDetail from "@/pages/SupplierDeliveryDetail";
import AcceptanceQueue from "@/pages/AcceptanceQueue";
import AcceptanceReview from "@/pages/AcceptanceReview";
// ── AP-INV-01: Digital Supplier Invoice Submission ────────────────────────────
import SupplierInvoices from "@/pages/SupplierInvoices";
import SupplierNewInvoice from "@/pages/SupplierNewInvoice";
import SupplierInvoiceDetail from "@/pages/SupplierInvoiceDetail";
// ── AP-MATCH-01: Finance Invoice Review Workspace ─────────────────────────────
import FinanceInvoiceQueue from "@/pages/FinanceInvoiceQueue";
import FinanceInvoiceReview from "@/pages/FinanceInvoiceReview";
// ── AP-PAY-01: Supplier Payment Recording, Allocation and Reconciliation ──────
import FinancePaymentWorkspace from "@/pages/FinancePaymentWorkspace";
import FinancePaymentDetail from "@/pages/FinancePaymentDetail";
import FinanceCreditNotes from "@/pages/FinanceCreditNotes";
// ── ERP-FIN-02: Customer AR — Gate AR-01 Invoice Foundation ───────────────────
import ARInvoiceWorkspace from "@/pages/ARInvoiceWorkspace";
import ARInvoiceDetail from "@/pages/ARInvoiceDetail";
// ── ERP-FIN-02: Customer AR — Gate AR-02 Receipts, Advances, Allocation ───────
import ARReceiptWorkspace from "@/pages/ARReceiptWorkspace";
import ARReceiptDetail from "@/pages/ARReceiptDetail";
import ARMasterDataAdmin from "@/pages/ARMasterDataAdmin";
// ── PAY-01B: Manual Collections Hub ───────────────────────────────────────────
import PayCollectionsHub from "@/pages/PayCollectionsHub";
// ── PAY-01C: Payment Plans, Installments and Mock Reminders ──────────────────
import ARPaymentPlanWorkspace from "@/pages/ARPaymentPlanWorkspace";
import ARPaymentPlanDetail from "@/pages/ARPaymentPlanDetail";
import GatewayPaymentsWorkspace from "@/pages/GatewayPaymentsWorkspace";
// ── ERP-FIN-02: Customer AR — Gate AR-03 Credit Notes & Refunds ────────────────
import ARCreditNoteWorkspace from "@/pages/ARCreditNoteWorkspace";
import ARCreditNoteDetail from "@/pages/ARCreditNoteDetail";
import ARRefundWorkspace from "@/pages/ARRefundWorkspace";
// ── ERP-FIN-02: Customer AR — Gate AR-04 Statements, Aging & Accounting Readiness
import ARCustomer360Workspace from "@/pages/ARCustomer360Workspace";
import ARAgingDashboard from "@/pages/ARAgingDashboard";
// ── ERP-FIN-03: GL-JOURNAL-01 General Ledger Journals ─────────────────────────
import GLJournalWorkspace from "@/pages/GLJournalWorkspace";
import GLJournalDetail from "@/pages/GLJournalDetail";
// GATE5-FIN-09: COA Activation Maker-Checker
import COAActivationWorkspace from "@/pages/COAActivationWorkspace";
// Public Demand Intelligence
import DemandAnalyticsDashboard from "@/pages/DemandAnalyticsDashboard";
// User Guide
import UserGuide from "@/pages/UserGuide";
// CRM-NAV-AUDIT-01: Notifications full-page view (DCC-03 feed)
import NotificationsPage from "@/pages/NotificationsPage";
// GQP-01: Pricing Studio
import PricingStudio from "@/pages/PricingStudio";
// ── VCI-P1 Slice 1: Venue Commerce & Intelligence (Internal UAT Only) ─────────
import VenueCatalogue from "@/pages/VenueCatalogue";
import Venue360Profile from "@/pages/Venue360Profile";
// ── VCI-P1 Slice 2: Venue Partner Application & Verification (Internal UAT Only)
import VenuePartnerApplications from "@/pages/VenuePartnerApplications";
import VenuePartnerApplicationDetail from "@/pages/VenuePartnerApplicationDetail";
import VenuePartnerApplyForm from "@/pages/VenuePartnerApplyForm";
// ── VCI-P1 Slice 3: Internal SEO-Ready Venue Profile (Internal Preview Only)
import VenuePublicProfile from "@/pages/VenuePublicProfile";
// ── VCI-P1 Slice 4: Destination Intelligence (Internal Hyderabad Preview Only)
import DestinationPreview from "@/pages/DestinationPreview";
// ── ARGOV Connect Phase 1: Native Meeting Management
import ConnectMeetings from "@/pages/ConnectMeetings";
import ConnectScheduleMeeting from "@/pages/ConnectScheduleMeeting";
import ConnectMeetingDetail from "@/pages/ConnectMeetingDetail";
// ── ARGOV Connect Phase 2: Native Video Conferencing
import ConferenceRoom from "@/pages/ConferenceRoom";
import ConferenceGuestJoin from "@/pages/ConferenceGuestJoin";

/**
 * AuthRedirect — routes fallback / alias paths to the correct login experience
 * based on the authoritative auth config. Never guesses the mode.
 */
function AuthRedirect() {
  const { uatConfig } = useAuth();

  if (uatConfig === undefined) {
    return (
      <div className="min-h-screen bg-[#F3F3F3] flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-[#0176D3] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (uatConfig?.error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F3F3F3] px-4">
        <div className="bg-white border border-[#E5E5E5] rounded-xl p-8 max-w-sm w-full text-center shadow-sm">
          <AlertTriangle className="w-8 h-8 text-rose-500 mx-auto mb-3" />
          <h2 className="text-base font-semibold text-[#181818] mb-2">Authentication Unavailable</h2>
          <p className="text-sm text-[#706E6B]">Auth configuration could not be loaded. Please refresh.</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-[#0176D3] text-white text-sm rounded-lg hover:bg-[#014486]"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }
  return <Navigate to="/login" replace />;
}

// Redirects users lacking a capability away from a protected page
function CapabilityRoute({ cap, children, redirectTo = "/home" }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (!hasCapability(user, cap)) return <Navigate to={redirectTo} replace />;
  return children;
}

// CONCIERGE-01: Mount widget on specific public pages
// Single instance — preserves session continuity across navigation.
const CONCIERGE_PAGES = ["/", "/event-journey", "/build-my-event", "/explore", "/find-events-like-mine"];
const CONCIERGE_ALIAS_DEFAULT = "prashasta";

function GlobalConcierge() {
  const location = useLocation();
  // Determine if concierge should show on this path
  const path = location.pathname;
  const isPublicInquiry = path.startsWith("/public/inquire/");
  const isPublicPage = CONCIERGE_PAGES.some(p => path === p || path.startsWith(p + "/"));
  if (!isPublicPage && !isPublicInquiry) return null;
  // Use alias from URL path (for /public/inquire/:alias) or default
  const alias = isPublicInquiry
    ? path.split("/public/inquire/")[1]?.split("/")[0] || CONCIERGE_ALIAS_DEFAULT
    : CONCIERGE_ALIAS_DEFAULT;
  return <ConciergeWidget alias={alias} />;
}

function App() {
  return (
    <BrandProvider>
      <AuthProvider>
        <LabelProvider>
        <BrowserRouter>
          <GlobalConcierge />
          <Routes>
            {/* ── Public routes ──────────────────────────────────────────────── */}
            <Route path="/"          element={<EventJourney />} />
            <Route path="/event-journey" element={<EventJourney />} />
            <Route path="/login"          element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password"  element={<ResetPassword />} />
            <Route path="/activate"        element={<ActivateAccount />} />
            <Route path="/auth/callback/google" element={<GoogleAuthCallback />} />
            <Route path="/signin"    element={<AuthRedirect />} />
            <Route path="/auth"      element={<AuthRedirect />} />
            <Route path="/uat-login" element={<UATRolePicker />} />
            <Route path="/verify-2fa" element={<Verify2FA />} />
            <Route path="/public/inquire/:alias" element={<PublicInquiry />} />
            {/* ── Prashasta Event Journey — Public Experience ─────────────────── */}
            <Route path="/build-my-event" element={<BuildMyEvent />} />
            {/* ── Prashasta Smart Event Studio — Adaptive Configurator (UX-01) ── */}
            <Route path="/smart-studio" element={<SmartStudio />} />
            {/* ── EXP-01 Inspiration Gallery (public, no auth) ─────────────────── */}
            <Route path="/explore" element={<ExplorePage />} />
            <Route path="/explore/:slug" element={<EventStoryPage />} />
            <Route path="/find-events-like-mine" element={<FindEventsPage />} />

            {/* ── Protected app routes ────────────────────────────────────────── */}
            <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
              {/* Internal UAT validation — requires crm.lead.read capability; customer roles excluded */}
              <Route path="/demo/one-engage" element={<CapabilityRoute cap="crm.lead.read"><DemoOneEngage /></CapabilityRoute>} />
              <Route path="/home"      element={<BusinessDashboard />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/journeys/:journeyId" element={<JourneyView />} />
              <Route path="/setup-studio"        element={<SetupStudio />} />
              {/* PRASHASTA-SETUP-02 Gate 3: D4 — event.ops.read matches backend require_capability("event.ops.read") on all GET operations */}
              <Route path="/event-ops-mdm"       element={<CapabilityRoute cap="event.ops.read"><EventOpsMDMSetup /></CapabilityRoute>} />
              {/* D4 — catalog.manage matches backend require_capability("catalog.manage") on all write operations */}
              <Route path="/homepage-admin"      element={<CapabilityRoute cap="catalog.manage"><HomepageAdmin /></CapabilityRoute>} />
              <Route path="/setup-studio/:code"  element={<SetupDomainDetail />} />
              {/* D4 — admin.users: backend uses require_roles(SETUP_MANAGER_ROLES) = [PLATFORM_ADMIN, TENANT_ADMIN, SETUP_ADMIN] */}
              <Route path="/organization"        element={<CapabilityRoute cap="admin.users"><OrganizationSetup /></CapabilityRoute>} />
              {/* D4 — ledger.read matches backend require_capability("ledger.read") */}
              <Route path="/finance"             element={<CapabilityRoute cap="ledger.read"><FinanceSetup /></CapabilityRoute>} />
              <Route path="/finance/setup"       element={<CapabilityRoute cap="ledger.read"><FinanceSetup /></CapabilityRoute>} />
              <Route path="/finance/dashboard"   element={<CapabilityRoute cap="financial.dashboard.view"><FinancialDashboard /></CapabilityRoute>} />
              <Route path="/finance/purchase-orders"     element={<CapabilityRoute cap="ap.invoice.view"><PurchaseOrderWorkspace /></CapabilityRoute>} />
              <Route path="/finance/purchase-orders/:poId" element={<CapabilityRoute cap="ap.invoice.view"><PurchaseOrderDetail /></CapabilityRoute>} />
              {/* D4 — admin.users: backend uses require_roles(SETUP_MANAGER_ROLES) */}
              <Route path="/event-model"         element={<CapabilityRoute cap="admin.users"><EventModelSetup /></CapabilityRoute>} />
              <Route path="/agent-governance"    element={<CapabilityRoute cap="admin.users"><AgentGovernanceSetup /></CapabilityRoute>} />
              {/* D4 — comms.channel.read matches backend require_capability("comms.channel.read") */}
              <Route path="/email-channel"       element={<CapabilityRoute cap="comms.channel.read"><EmailChannelSetup /></CapabilityRoute>} />
              <Route path="/administration/communications" element={<CommunicationsAdmin />} />
              {/* PRASHASTA-SETUP-02 Gate 3: centralized Business Setup hub — admin.users (System Administrator only) */}
              <Route path="/business-setup"      element={<CapabilityRoute cap="admin.users"><BusinessSetup /></CapabilityRoute>} />
              {/* CRM-MDM-01: CRM Configuration (urgency thresholds + Co-Pilot guidance) — admin.users */}
              <Route path="/crm-configuration"   element={<CapabilityRoute cap="admin.users"><CRMConfiguration /></CapabilityRoute>} />
              <Route path="/events"              element={<Events />} />
              <Route path="/customers"           element={<Customers />} />
              <Route path="/administration"          element={<CapabilityRoute cap="admin.users"><Administration /></CapabilityRoute>} />
              <Route path="/administration/users"    element={<CapabilityRoute cap="admin.users"><Administration /></CapabilityRoute>} />
              <Route path="/administration/audit"    element={<CapabilityRoute cap="admin.users"><Administration /></CapabilityRoute>} />
              <Route path="/administration/integrations" element={<CapabilityRoute cap="integration.view"><IntegrationControlCenter /></CapabilityRoute>} />
              <Route path="/administration/imports"  element={<CapabilityRoute cap="admin.users"><ImportManager /></CapabilityRoute>} />
              {/* UEIM-00 — Enterprise Structure */}
              <Route path="/enterprise"              element={<CapabilityRoute cap="enterprise.read"><EnterpriseAdmin /></CapabilityRoute>} />
              <Route path="/enterprise/:tab"         element={<CapabilityRoute cap="enterprise.read"><EnterpriseAdmin /></CapabilityRoute>} />
              {/* Stage 01 */}
              <Route path="/engage"                        element={<EngageDashboard />} />
              <Route path="/engage/leads"                  element={<Leads />} />
              <Route path="/engage/leads/:leadId"          element={<LeadDetail />} />
              <Route path="/engage/inbox"                  element={<Inbox />} />
              <Route path="/engage/follow-ups"             element={<FollowUps />} />
              {/* ONE Engage CRM */}
              <Route path="/crm/leads"                     element={<Navigate to="/engage/leads" replace />} />
              <Route path="/crm/accounts"                  element={<CRMAccounts />} />
              <Route path="/crm/accounts/:accountId"       element={<CRMAccountDetail />} />
              <Route path="/crm/contacts"                  element={<CRMContacts />} />
              <Route path="/crm/contacts/:contactId"       element={<CRMContactDetail />} />
              <Route path="/crm/opportunities"             element={<CRMOpportunities />} />
              <Route path="/crm/opportunities/:oppId"      element={<OpportunityDetail />} />
              <Route path="/crm/activities"                element={<CRMActivities />} />
              <Route path="/crm/inbox"                     element={<CommunicationInbox />} />
              {/* Wave A Sprint 2: A08 Bookings */}
              <Route path="/crm/bookings"                  element={<CapabilityRoute cap="booking.read"><BookingsList /></CapabilityRoute>} />
              <Route path="/crm/bookings/:bookingId"       element={<CapabilityRoute cap="booking.read"><BookingDetail /></CapabilityRoute>} />
              {/* Wave A Sprint 2: A06 Service Catalog */}
              <Route path="/crm/catalog"                   element={<CapabilityRoute cap="catalog.read"><ServiceCatalog /></CapabilityRoute>} />
              {/* UEIM-02: Structured Sourcing */}
              <Route path="/enterprise/suppliers"          element={<CapabilityRoute cap="supplier.read"><SupplierRegistry /></CapabilityRoute>} />
              <Route path="/crm/partners"                  element={<CapabilityRoute cap="supplier.read"><PartnerDirectory /></CapabilityRoute>} />
              <Route path="/crm/sourcing-packages/:packageId" element={<CapabilityRoute cap="sourcing.read"><SourcingPackageDetail /></CapabilityRoute>} />
              {/* UEIM-04: Event Core Workspace */}
              <Route path="/crm/events/:eid"               element={<CapabilityRoute cap="event.read"><EventMasterWorkspace /></CapabilityRoute>} />
              <Route path="/crm/events/:eid/operations"  element={<CapabilityRoute cap="event.ops.read"><EventOperationsWorkspace /></CapabilityRoute>} />
              <Route path="/crm/events/:eid/closeout"   element={<CapabilityRoute cap="event.closeout.read"><EventCloseoutWorkspace /></CapabilityRoute>} />
              <Route path="/crm/events/:eid/documents"  element={<CapabilityRoute cap="document.read"><EventDocumentWorkspace /></CapabilityRoute>} />
              {/* WVB-02: Event Financial Budget Workspace */}
              <Route path="/crm/events/:eid/financials" element={<CapabilityRoute cap="budget.view"><EventFinancialsWorkspace /></CapabilityRoute>} />
              {/* WVB-03: Approval Policy Administration */}
              <Route path="/settings/approval-policies" element={<CapabilityRoute cap="budget.view"><ApprovalPolicyAdminPage /></CapabilityRoute>} />
              {/* Public Demand Intelligence */}
              <Route path="/marketing/public-portal/analytics" element={<CapabilityRoute cap="marketing.analytics.read"><DemandAnalyticsDashboard /></CapabilityRoute>} />
              {/* Stage 02 */}
              <Route path="/imagine"                       element={<ImagineDashboard />} />
              <Route path="/imagine/events/:eventId"       element={<EventImagine />} />
              {/* Stage 03 */}
              <Route path="/events/:eventId/threads"       element={<ThreadsView />} />
              <Route path="/events/:eventId/360"           element={<Event360 />} />
              {/* EO-01 */}
              <Route path="/events/:eventId/changes/:changeId" element={<EventChangeDetail />} />
              {/* UAT */}
              <Route path="/uat" element={<UATControlCenter />} />
              {/* Event Planner / Designer */}
              <Route path="/planner/cases" element={<PlannerCases />} />
              {/* User Guide — universal */}
              <Route path="/guide" element={<UserGuide />} />
              {/* CRM-NAV-AUDIT-01: Notifications full-page view (DCC-03 feed, /notifications) */}
              <Route path="/notifications" element={<NotificationsPage />} />
              {/* GQP-01: Pricing Studio — accessed from Setup Studio domain 08-PRICING-STUDIO */}
              <Route path="/pricing-studio" element={<CapabilityRoute cap="pricing.read"><PricingStudio /></CapabilityRoute>} />

              {/* ── AP-ACCEPT-01: Internal Operations Acceptance Queue ─────── */}
              <Route path="/ap/acceptance" element={<CapabilityRoute cap="ap.invoice.view"><AcceptanceQueue /></CapabilityRoute>} />
              <Route path="/ap/acceptance/:submissionId" element={<CapabilityRoute cap="ap.invoice.view"><AcceptanceReview /></CapabilityRoute>} />
              {/* ── AP-MATCH-01: Finance Invoice Review Workspace ──────────── */}
              <Route path="/ap/invoice-review" element={<CapabilityRoute cap="ap.invoice.view"><FinanceInvoiceQueue /></CapabilityRoute>} />
              <Route path="/ap/invoice-review/:invoiceId" element={<CapabilityRoute cap="ap.invoice.view"><FinanceInvoiceReview /></CapabilityRoute>} />
              {/* ── AP-PAY-01: Supplier Payment Workspace ──────────────────── */}
              <Route path="/ap/payments" element={<CapabilityRoute cap="ap.invoice.view"><FinancePaymentWorkspace /></CapabilityRoute>} />
              <Route path="/ap/payments/:paymentId" element={<CapabilityRoute cap="ap.invoice.view"><FinancePaymentDetail /></CapabilityRoute>} />
              <Route path="/ap/credit-notes" element={<CapabilityRoute cap="ap.invoice.view"><FinanceCreditNotes /></CapabilityRoute>} />

              {/* ── ERP-FIN-02: Customer AR — Gate AR-01 Invoice Foundation ── */}
              <Route path="/ar/invoices" element={<CapabilityRoute cap="ar.invoice.view"><ARInvoiceWorkspace /></CapabilityRoute>} />
              <Route path="/ar/invoices/:invoiceId" element={<CapabilityRoute cap="ar.invoice.view"><ARInvoiceDetail /></CapabilityRoute>} />

              {/* ── ERP-FIN-02: Customer AR — Gate AR-02 Receipts, Advances, Allocation ── */}
              <Route path="/ar/receipts" element={<CapabilityRoute cap="ar.receipt.view"><ARReceiptWorkspace /></CapabilityRoute>} />
              <Route path="/ar/receipts/:receiptId" element={<CapabilityRoute cap="ar.receipt.view"><ARReceiptDetail /></CapabilityRoute>} />
              <Route path="/ar/master-data" element={<CapabilityRoute cap="ar.receipt.view"><ARMasterDataAdmin /></CapabilityRoute>} />
              {/* PAY-01B: Manual Collections Hub */}
              <Route path="/ar/collections" element={<CapabilityRoute cap="ar.receipt.view"><PayCollectionsHub /></CapabilityRoute>} />
              {/* PAY-01C: Payment Plans */}
              <Route path="/ar/payment-plans" element={<CapabilityRoute cap="ar.plan.view"><ARPaymentPlanWorkspace /></CapabilityRoute>} />
              <Route path="/ar/payment-plans/:planId" element={<CapabilityRoute cap="ar.plan.view"><ARPaymentPlanDetail /></CapabilityRoute>} />
              <Route path="/ar/gateway-payments" element={<CapabilityRoute cap="payment.gateway.view"><GatewayPaymentsWorkspace /></CapabilityRoute>} />

              {/* ── ERP-FIN-02: Customer AR — Gate AR-03 Credit Notes & Refunds ── */}
              <Route path="/ar/credit-notes" element={<CapabilityRoute cap="ar.credit_note.view"><ARCreditNoteWorkspace /></CapabilityRoute>} />
              <Route path="/ar/credit-notes/:cnId" element={<CapabilityRoute cap="ar.credit_note.view"><ARCreditNoteDetail /></CapabilityRoute>} />
              <Route path="/ar/refunds" element={<CapabilityRoute cap="ar.refund.request"><ARRefundWorkspace /></CapabilityRoute>} />

              {/* ── ERP-FIN-02 Gate AR-04: Statements, Aging & Accounting Readiness ── */}
              <Route path="/ar/aging" element={<CapabilityRoute cap="ar.aging.view"><ARAgingDashboard /></CapabilityRoute>} />
              <Route path="/ar/customers/:cid/statement" element={<CapabilityRoute cap="ar.statement.view"><ARCustomer360Workspace /></CapabilityRoute>} />

              {/* ── ERP-FIN-03: GL Journal (GL-JOURNAL-01) ─────────────────── */}
              <Route path="/gl/journals"       element={<CapabilityRoute cap="ledger.read"><GLJournalWorkspace /></CapabilityRoute>} />
              <Route path="/gl/journals/:journalId" element={<CapabilityRoute cap="ledger.read"><GLJournalDetail /></CapabilityRoute>} />
              <Route path="/finance/coa-activation" element={<CapabilityRoute cap="coa.read"><COAActivationWorkspace /></CapabilityRoute>} />

              {/* ── VCI-P1 Slice 1: Venue Commerce (Internal UAT Only) ──────── */}
              <Route path="/venue-commerce/catalogue" element={<CapabilityRoute cap="supplier.read"><VenueCatalogue /></CapabilityRoute>} />
              {/* ── VCI-P1 Slice 2: Venue Partner Applications (Internal UAT Only) ─ */}
              {/* NOTE: /partner-applications routes MUST come before /:vid catch-all */}
              <Route path="/venue-commerce/partner-applications" element={<CapabilityRoute cap="supplier.read"><VenuePartnerApplications /></CapabilityRoute>} />
              <Route path="/venue-commerce/partner-applications/new" element={<CapabilityRoute cap="supplier.manage"><VenuePartnerApplyForm /></CapabilityRoute>} />
              <Route path="/venue-commerce/partner-applications/:aid" element={<CapabilityRoute cap="supplier.read"><VenuePartnerApplicationDetail /></CapabilityRoute>} />
              <Route path="/venue-commerce/:vid" element={<CapabilityRoute cap="supplier.read"><Venue360Profile /></CapabilityRoute>} />
              {/* ── VCI-P1 Slice 3: Venue Preview (Internal SEO Profile) ──────── */}
              {/* NOTE: /venue-preview/:slug is separate from /venue-commerce/:vid  */}
              <Route path="/venue-preview/:slug" element={<CapabilityRoute cap="supplier.read"><VenuePublicProfile /></CapabilityRoute>} />
              {/* ── VCI-P1 Slice 4: Destination Intelligence (Internal Preview Only) ── */}
              {/* noindex + supplier.read enforced; no public SEO activation in Phase 1 */}
              <Route path="/destination-preview/:slug" element={<CapabilityRoute cap="supplier.read"><DestinationPreview /></CapabilityRoute>} />
              {/* ── ARGOV Connect Phase 1: Meeting Management ────────────────── */}
              <Route path="/connect/meetings" element={<CapabilityRoute cap="meeting.read"><ConnectMeetings /></CapabilityRoute>} />
              <Route path="/connect/schedule" element={<CapabilityRoute cap="meeting.schedule"><ConnectScheduleMeeting /></CapabilityRoute>} />
              <Route path="/connect/meetings/:mid" element={<CapabilityRoute cap="meeting.read"><ConnectMeetingDetail /></CapabilityRoute>} />
              {/* ── ARGOV Connect Phase 2: Native Video Conferencing ─────────── */}
              {/* Authenticated conference room — conference.join required */}
              <Route path="/connect/meetings/:mid/conference" element={<CapabilityRoute cap="conference.join"><ConferenceRoom /></CapabilityRoute>} />
            </Route>

            {/* ── ARGOV Connect Phase 2: Public guest join (no platform login) ── */}
            {/* Protected by invitation validation, expiry, revocation and room-scoped token — not open access */}
            <Route path="/guest/join/:invToken" element={<ConferenceGuestJoin />} />

            {/* ── Public supplier portal — no auth required ──────────────── */}
            <Route path="/sourcing/quote/:token" element={<SupplierQuoteForm />} />

            {/* ── AP-PORTAL-01: Supplier Partner Portal ──────────────────── */}
            <Route path="/portal/login" element={<SupplierLogin />} />
            <Route path="/portal/activate" element={<SupplierPortalActivate />} />
            <Route
              path="/portal/dashboard"
              element={
                <PortalProtectedRoute>
                  <SupplierPortalDashboard />
                </PortalProtectedRoute>
              }
            />
            <Route
              path="/portal/po/:poId"
              element={
                <PortalProtectedRoute>
                  <SupplierPODetail />
                </PortalProtectedRoute>
              }
            />

            {/* ── AP-ACCEPT-01: Supplier Delivery Evidence (Portal) ───────── */}
            <Route
              path="/portal/deliveries"
              element={
                <PortalProtectedRoute>
                  <SupplierDeliveries />
                </PortalProtectedRoute>
              }
            />
            <Route
              path="/portal/deliveries/submit"
              element={
                <PortalProtectedRoute>
                  <SupplierSubmitDelivery />
                </PortalProtectedRoute>
              }
            />
            <Route
              path="/portal/deliveries/:submissionId"
              element={
                <PortalProtectedRoute>
                  <SupplierDeliveryDetail />
                </PortalProtectedRoute>
              }
            />

            {/* ── AP-INV-01: Supplier Invoice Submission ─────────────────── */}
            <Route
              path="/portal/invoices"
              element={
                <PortalProtectedRoute>
                  <SupplierInvoices />
                </PortalProtectedRoute>
              }
            />
            <Route
              path="/portal/invoices/new"
              element={
                <PortalProtectedRoute>
                  <SupplierNewInvoice />
                </PortalProtectedRoute>
              }
            />
            <Route
              path="/portal/invoices/:invoiceId"
              element={
                <PortalProtectedRoute>
                  <SupplierInvoiceDetail />
                </PortalProtectedRoute>
              }
            />

            {/* ── Fallback: all unknown routes → correct login experience ─── */}
            <Route path="*" element={<AuthRedirect />} />
          </Routes>
          <Toaster richColors position="top-right" />
        </BrowserRouter>
        </LabelProvider>
      </AuthProvider>
    </BrandProvider>
  );
}

export default App;
